import { Helmet } from "react-helmet";

const Privacy = () => {
  return (
      <>
        <Helmet>
          <title>Privacy Policy | SurpriseTable</title>
          <meta
              name="description"
              content="Learn how SurpriseTable by Deviofy protects your privacy and personal data. Read our full privacy policy covering collection, use, storage, and your legal rights."
          />
        </Helmet>
        <section className="bg-white py-16 transition-colors duration-200 dark:bg-gray-900">
          <div className="container mx-auto max-w-3xl px-4">
            <h1 className="mb-8 font-serif text-4xl font-bold text-gray-800 dark:text-white text-center">
              Privacy Policy
            </h1>
            <div className="space-y-8 text-gray-700 dark:text-gray-200 text-base">

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  1. Introduction
                </h2>
                <p>
                  Welcome to SurpriseTable, a product of <b>Deviofy FZCO</b>. Your privacy and trust are important to us.
                  This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our website, mobile app, or services.
                  By using SurpriseTable, you agree to the practices described here.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  2. Who We Are
                </h2>
                <p>
                  SurpriseTable is operated by <b>Deviofy FZCO</b>, registered in Dubai Silicon Oasis, DDP, Building A2, Dubai, UAE.
                  For privacy matters, contact our Data Protection Officer at <a href="mailto:info@deviofy.com" className="text-primary hover:underline">info@deviofy.com</a>.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  3. What Data We Collect
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <b>Personal information:</b> Name, email, phone number, address (for bookings and support).
                  </li>
                  <li>
                    <b>Booking details:</b> Date, location preferences, group size, dietary restrictions, special requests.
                  </li>
                  <li>
                    <b>Payment info:</b> Processed securely via third-party payment providers (Stripe, PayPal, etc.). We do <b>not</b> store your card or bank details.
                  </li>
                  <li>
                    <b>Usage data:</b> IP address, device information, browser details, session logs, and interaction data (for analytics and security).
                  </li>
                  <li>
                    <b>Feedback & communication:</b> Reviews, support requests, referrals, and other messages you send us.
                  </li>
                  <li>
                    <b>Referral data:</b> If you invite friends or join via a referral link, we record this for credit and anti-abuse purposes.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  4. How We Use Your Data
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To process bookings and deliver our surprise dining service.</li>
                  <li>To personalize your experience, match you with suitable restaurants, and remember your preferences.</li>
                  <li>To process payments, issue refunds, and manage credits securely.</li>
                  <li>To send booking confirmations, updates, offers, and essential communication.</li>
                  <li>To analyze site usage, prevent fraud, and improve our services.</li>
                  <li>To fulfill legal, regulatory, or accounting obligations in the UAE and international markets (including GDPR, if applicable).</li>
                </ul>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  5. Sharing Your Data
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <b>With partner restaurants:</b> We may share your name, contact, and dietary info with your booked restaurant so they can provide the best service.
                  </li>
                  <li>
                    <b>With payment processors:</b> (e.g., Stripe, PayPal) to handle transactions securely.
                  </li>
                  <li>
                    <b>With trusted service providers:</b> (hosting, analytics, communications) who must follow strict privacy and security standards.
                  </li>
                  <li>
                    <b>For legal reasons:</b> If required by law, regulation, court order, or to enforce our terms and policies.
                  </li>
                  <li>
                    <b>We NEVER sell or rent your data.</b>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  6. International Transfers
                </h2>
                <p>
                  Your data may be processed and stored in the UAE, European Union, or other countries.
                  We ensure all transfers meet applicable data protection standards (GDPR, UAE PDPL, etc.).
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  7. Data Retention
                </h2>
                <p>
                  We keep your personal data only as long as needed for bookings, support, legal, and business purposes. You may request deletion of your data at any time, unless we are required to retain it by law.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  8. Cookies & Analytics
                </h2>
                <p>
                  SurpriseTable uses cookies and similar technologies to keep you logged in, remember preferences, and analyze website usage.
                  Third-party analytics tools (like Google Analytics) may process anonymized data for statistics. You can control cookies in your browser settings.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  9. Your Rights (GDPR & UAE PDPL)
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Right to access your data.</li>
                  <li>Right to correct or update data.</li>
                  <li>Right to request deletion ("right to be forgotten").</li>
                  <li>Right to restrict or object to processing.</li>
                  <li>Right to data portability (export your data).</li>
                  <li>Right to withdraw consent at any time.</li>
                  <li>Right to lodge a complaint with your data protection authority (EU or UAE).</li>
                </ul>
                <p>
                  To exercise any of these rights, email us at <a href="mailto:info@deviofy.com" className="text-primary hover:underline">info@deviofy.com</a>.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  10. Children's Privacy
                </h2>
                <p>
                  SurpriseTable is not intended for use by children under 16. We do not knowingly collect data from minors. If you believe a child has provided us with personal data, please contact us for removal.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  11. Data Security
                </h2>
                <p>
                  We use technical and organizational measures to safeguard your personal data from unauthorized access, disclosure, or misuse.
                  Our systems comply with best industry standards and relevant laws.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  12. Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy periodically. Significant changes will be communicated by email, in-app notice, or via this website.
                  Please review this policy regularly for the latest version.
                </p>
              </div>

              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  13. Contact Us
                </h2>
                <p>
                  If you have any questions, requests, or complaints regarding your privacy or this policy, please email us at <a href="mailto:info@deviofy.com" className="text-primary hover:underline">info@deviofy.com</a>.
                  Our mailing address is: Deviofy FZCO, DSO-THUB, Dubai Digital Park, Dubai Silicon Oasis, Building A2, Dubai, UAE.
                </p>
              </div>

            </div>
          </div>
        </section>
      </>
  );
};

export default Privacy;
